import { type FC, useState } from "react";

import { Button, Small } from "@fronterahealth/frontera-ui-components";

import { type RulePackage, useListPayorTypes } from "@api/rest/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { ImportPackagesDialog } from "@pages/QARules/ImportPackagesDialog";
import { PackageCard } from "@pages/QARules/PackageCard";
import { PackageDetailsForm } from "@pages/QARules/PackageDetailsForm";
import { PACKAGE_TYPE_FILTER_ALL, PackageType } from "@pages/QARules/constants";
import { PACKAGE_TYPE_BADGE, type PackageJson } from "@pages/QARules/qaRuleLabels";
import { useQARuleApi } from "@pages/QARules/useQARuleApi";

interface PackagesTabProps {
  packages: RulePackage[];
  isLoading: boolean;
  showArchived: boolean;
  setShowArchived: (show: boolean) => void;
}

export const PackagesTab: FC<PackagesTabProps> = ({ packages, isLoading, showArchived, setShowArchived }) => {
  const qaRuleApi = useQARuleApi();
  const { data: payorData } = useListPayorTypes();
  const payorTypes = payorData?.status === 200 ? payorData.data.results : [];

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>(PACKAGE_TYPE_FILTER_ALL);
  const [creating, setCreating] = useState(false);
  const [creatingPending, setCreatingPending] = useState(false);
  const [importing, setImporting] = useState(false);

  const query = search.trim().toLowerCase();
  const visible = packages.filter(
    (pkg) =>
      (typeFilter === PACKAGE_TYPE_FILTER_ALL || pkg.package_type === typeFilter) &&
      (!query ||
        pkg.name.toLowerCase().includes(query) ||
        pkg.rules.some((rule) => rule.name.toLowerCase().includes(query))),
  );

  const handleImport = async (imported: PackageJson[]) => {
    await qaRuleApi.importPackages(imported);
  };

  const filterButton = (value: string, label: string) => (
    <button
      key={value}
      type="button"
      onClick={() => setTypeFilter(value)}
      className={
        typeFilter === value
          ? "rounded-full bg-globemallow-600 px-3 py-1 text-xs font-medium text-white"
          : "rounded-full border border-border-medium px-3 py-1 text-xs font-medium text-text-secondary hover:border-globemallow-600"
      }
    >
      {label}
    </button>
  );

  return (
    <div className="flex max-w-5xl flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          aria-label="Search packages"
          placeholder="Search packages and rules"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-72 rounded border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-globemallow-600"
        />
        <div className="flex gap-1.5">
          {filterButton(PACKAGE_TYPE_FILTER_ALL, "All")}
          {Object.values(PackageType).map((type) => filterButton(type, PACKAGE_TYPE_BADGE[type].label))}
        </div>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived
        </label>
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" buttonStyle="border" size="sm" text="Import" onClick={() => setImporting(true)} />
          <Button
            variant="primary"
            buttonStyle="filled"
            size="sm"
            text="+ New package"
            onClick={() => setCreating(true)}
          />
        </div>
      </div>

      {creating && (
        <PackageDetailsForm
          idPrefix="new-package"
          payorTypes={payorTypes}
          submitText="Create package"
          isPending={creatingPending}
          onCancel={() => setCreating(false)}
          onSubmit={async (fields) => {
            setCreatingPending(true);
            try {
              await qaRuleApi.createPackage(fields);
              setCreating(false);
            } catch (e) {
              notifyError(e instanceof Error ? e.message : "Could not create package.");
            } finally {
              setCreatingPending(false);
            }
          }}
        />
      )}

      {isLoading && <Small colorType="secondary">Loading...</Small>}
      {!isLoading && visible.length === 0 && <Small colorType="secondary">No packages match.</Small>}
      <Small colorType="secondary">
        {visible.length} of {packages.length} package{packages.length === 1 ? "" : "s"}
      </Small>
      <div className="flex flex-col gap-3">
        {visible.map((pkg) => (
          <PackageCard key={pkg.id} pkg={pkg} payorTypes={payorTypes} linkToDetail />
        ))}
      </div>

      {importing && <ImportPackagesDialog open={importing} setOpen={setImporting} onImport={handleImport} />}
    </div>
  );
};
